import { makeAutoObservable } from "mobx";
import {
  DocumentLanguage,
  isOk,
  LocalizedText,
  Result,
  TranslationOption,
} from "../../common/common";
import {
  documentLanguageFieldModel,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../../common/commonForms";
import { DocumentType } from "../../common/documents";
import { Language } from "../../i18n/languages";
import {
  DbotEvent,
  DbotEventDispatcher,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  getProjectCompanies,
  isDocumentSentForSigning,
  isDocumentSigned,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  ProjectDocumentStatus,
  Recipient,
  ResendDocumentForViewing,
  Subscriber,
  updateCompanyRoleEmailsEvent,
  UpdateCompanyRoleEmailsResultEvent,
} from "../common/commonDomain";
import { compact, first, get, identity, partial, reject, remove, sumBy } from "lodash";
import { TMessage } from "../../i18n/tt";
import { when } from "../../utils/functions";
import { move } from "../../utils/arrays";
import { newUuid } from "../../utils/uuid";
import { LocalizedTextEditorModel } from "../../text/localizedTextEditorDomain";
import { getRoleTypeLabel, RoleType } from "../../types/enums/RoleType";
import {
  downloadSignedDocumentAction,
  openSendForSigningDialogAction,
  RecipientFieldModel,
  RecipientsEditModel,
} from "../summary/summaryDomain";
import {
  resolveUrnReference,
  toCompanyRoleReference,
  toShareholderReference,
  toSubscriberIdReference,
} from "../../utils/UrnReference";
import { getFullName, isBoardMember, sortedRoles } from "../../utils/Roles";
import { ExtendedShareholderDTO } from "../../types/Shareholder";
import { CompanyRole } from "../../types/CompanyInfo";
import { filterActiveShareholders } from "../../stores/ShareholdersStore";
import { AlertProps } from "../../components-v2/Alert";
import { TransactionType } from "../../types/enums/TransactionType";
import { Transaction } from "../../types/Transactions";
import { isAfter, isBefore } from "../../utils/Dates";
import { DropdownMenuItems, DropdownMenuProps } from "../../components-v2/Dropdown";
import { DecisionType } from "../../common/decisions";
import { isExtensionPdf } from "../../utils/files";

export type CustomDocumentInsideAddressOption = "recipient" | "freetext";

export type CustomDocumentDateOption = "sendoutDate" | "custom";

export type CustomDocumentType = "letter" | "agreement";

export const customDocumentTypes: CustomDocumentType[] = ["letter", "agreement"];

export type CustomDocumentData = {
  type: DocumentType.custom_document;
  blocks?: CustomDocumentBlock[];
  customDocumentDate?: string;
  documentDateOption?: CustomDocumentDateOption;
  documentLanguage?: Language[];
  insideAddressEnabled?: boolean;
  insideAddressFreetext?: string;
  insideAddressOption?: CustomDocumentInsideAddressOption;
  headerLogoEnabled?: boolean;
  headerCompanyNameEnabled?: boolean;
  headerDateEnabled?: boolean;
  headerTitlesEnabled?: boolean;
  headerEnabled?: boolean;
  recipients?: Recipient[];
  signatureEnabled?: boolean;
  signatureSendersStatement?: LocalizedText[];
  signatureSenders?: CustomDocumentSignatory[];
  signatoriesStatement?: LocalizedText[];
  signatories?: CustomDocumentSignatory[];
  title?: LocalizedText[];
  translationOption?: TranslationOption;
};

export type CustomDocumentOverrides = {
  type: DocumentType.custom_document;
  recipients?: Recipient[];
};

export type CustomDocumentBlock =
  | CustomDocumentFreetextBlock
  | CustomDocumentShareholdersBlock
  | CustomDocumentTransactionLogBlock
  | CustomDocumentPersonalDrawdownContributionBlock;

export type CustomDocumentFreetextBlock = {
  type: "freetext";
  id: string;
  heading: LocalizedText[];
  text: LocalizedText[];
};

export type CustomDocumentShareholdersBlock = {
  type: "shareholders";
  id: string;
  perDate?: string;
};

export type CustomDocumentTransactionLogBlock = {
  type: "transactionLog";
  id: string;
  heading: LocalizedText[];
  from: string;
  to: string;
  transactionTypes: TransactionType[];
};

export type CustomDocumentPersonalDrawdownContributionBlock = {
  type: "personalDrawdownContribution";
  id: string;
  heading: LocalizedText[];
};

export const customBlockLabels: { [key in CustomDocumentBlock["type"]]: TMessage } = {
  freetext: { key: "dbot.custom-document.blocks.freetext.label" },
  shareholders: { key: "dbot.custom-document.blocks.shareholders.label" },
  transactionLog: { key: "dbot.custom-document.blocks.transactions.label" },
  personalDrawdownContribution: {
    key: "dbot.custom-document.blocks.personal-drawdown-contribution.label",
  },
};

export type CustomDocumentSignatoryOption =
  | "company"
  | "ceo"
  | "chairperson"
  | "freetext"
  | "recipient";

export type CustomDocumentSignatory = {
  id: string;
  option: CustomDocumentSignatoryOption;
  freetextName?: string;
  freetextRole?: LocalizedText[];
};

export type CustomDocumentBlockForm =
  | CustomDocumentFreetextBlockForm
  | CustomDocumentShareholdersBlockForm
  | CustomDocumentTransactionLogBlockForm
  | CustomDocumentPersonalDrawdownContributionBlockForm;

export type CustomDocumentFreetextBlockForm = {
  type: "freetext";
  id: string;
  heading: LocalizedTextEditorModel;
  text: LocalizedTextEditorModel;
};

export type CustomDocumentShareholdersBlockForm = {
  type: "shareholders";
  id: string;
  currentOrPerDate: RadioFieldModel<"current" | "per-date">;
  perDate: LocalDateFieldModel;
  showDateInput: boolean;
};

export type CustomDocumentTransactionLogBlockForm = {
  type: "transactionLog";
  id: string;
  heading: LocalizedTextEditorModel;
  from: LocalDateFieldModel;
  to: LocalDateFieldModel;
  transactionTypes: OptionFieldModel<TransactionType>;
  initTransactionTypes: TransactionType[];
};

export type CustomDocumentPersonalDrawdownContributionBlockForm = {
  type: "personalDrawdownContribution";
  id: string;
  heading: LocalizedTextEditorModel;
};

export type CustomDocumentSignatoryForm = {
  id: string;
  option: CustomDocumentSignatoryOption;
  freetextName: StringFieldModel;
  freetextRole: LocalizedTextEditorModel;
};

export type SendCustomDocumentDialogModel = {
  recipients: RecipientsEditModel;
  title: (lang: Language) => TMessage | undefined;
  emailBody: LocalizedTextEditorModel;
  alert: () => AlertProps | undefined;
  isSubmitting?: boolean;
  onClose?: () => void;
  onSubmit: () => void;
};

export const customDocumentTypeLabels: Record<CustomDocumentType, TMessage> = {
  agreement: { key: "dbot.custom-document.type.agreement" },
  letter: { key: "dbot.custom-document.type.letter" },
};

export const newCustomDocumentTypeLabels: Record<CustomDocumentType, TMessage> = {
  agreement: { key: "dbot.custom-document.untitled-agreement" },
  letter: { key: "dbot.custom-document.untitled-letter" },
};

export type QuickAddItem = {
  name: string;
  label: TMessage;
  count?: number;
  disabled: boolean;
  recipients: Recipient[];
};

export class CustomDocumentForm {
  readonly type = DocumentType.custom_document;
  documentLanguage: RadioFieldModel<DocumentLanguage>;
  title: LocalizedTextEditorModel;
  insideAddressEnabled: boolean;
  insideAddressOption: RadioFieldModel<CustomDocumentInsideAddressOption>;
  insideAddressFreetext: StringFieldModel;
  headerLogoEnabled: boolean;
  headerCompanyNameEnabled: boolean;
  headerTitlesEnabled: boolean;
  headerDateEnabled: boolean;
  headerEnabled: boolean;
  documentDateOption: RadioFieldModel<CustomDocumentDateOption>;
  customDocumentDate: LocalDateFieldModel;
  blocks: CustomDocumentBlockForm[];
  signatureEnabled: boolean;
  signatureSendersStatement: LocalizedTextEditorModel;
  signatureSenders: CustomDocumentSignatoryForm[];
  signatoriesStatement: LocalizedTextEditorModel;
  signatories: CustomDocumentSignatoryForm[];

  sendDocumentDialogModel?: SendCustomDocumentDialogModel;
  transactionLog: Transaction[] = [];

  customDocumentTypeHint?: TMessage;
  selectedCompany?: string;
  recipientUpdateResult?: Result<TMessage, TMessage>;
  recipientsByCompany: {
    [orgNo: string]: RecipientsEditModel;
  } = {};

  constructor(
    readonly dispatch: DbotEventDispatcher,
    readonly context: DocumentContext<CustomDocumentData, CustomDocumentOverrides>,
    options?: Record<string, unknown>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.documentLanguage = documentLanguageFieldModel({
      orgNo: context.orgNo,
      value: mapLocalesToDocumentLanguage(context.documentData.documentLanguage),
      onChange: (lang) => this.onLanguageChange(mapDocumentLanguageToLocales(lang)),
    });
    this.title = new LocalizedTextEditorModel({
      texts: context.documentData.title,
      aiClient: context.aiClient,
      isReadOnly: () => context.isLocked,
      label: { key: "title" },
    });
    this.headerLogoEnabled = context.documentData.headerLogoEnabled ?? true;
    this.headerCompanyNameEnabled = context.documentData.headerCompanyNameEnabled ?? true;
    this.headerTitlesEnabled = context.documentData.headerTitlesEnabled ?? true;
    this.headerDateEnabled = context.documentData.headerDateEnabled ?? true;
    this.headerEnabled = context.documentData.headerEnabled ?? true;
    this.insideAddressEnabled = context.documentData.insideAddressEnabled ?? true;
    this.insideAddressOption = new RadioFieldModel({
      options: ["recipient", "freetext"],
      value: context.documentData.insideAddressOption ?? "recipient",
      idFn: identity,
      labelFn: (x) => {
        switch (x) {
          case "recipient":
            return { key: "dbot.custom-document.recipient-auto" };
          default:
            return { key: "dbot.freetext" };
        }
      },
    });
    this.insideAddressFreetext = new StringFieldModel({
      value: context.documentData.insideAddressFreetext,
    });
    this.documentDateOption = new RadioFieldModel({
      options: ["sendoutDate", "custom"],
      value: context.documentData.documentDateOption ?? "sendoutDate",
      idFn: identity,
      labelFn: (x) => {
        switch (x) {
          case "sendoutDate":
            return { key: "dbot.custom-document.sendout-date-auto" };
          case "custom":
            return { key: "dbot.custom-document.custom-date" };
        }
      },
    });
    this.customDocumentDate = new LocalDateFieldModel({
      value: context.documentData.customDocumentDate,
    });
    this.blocks =
      context.documentData.blocks?.map(
        partial(initBlockForm, context, this.transactionLog, this),
      ) ?? [];
    this.signatureEnabled = context.documentData.signatureEnabled ?? true;
    this.signatureSendersStatement = new LocalizedTextEditorModel({
      texts: context.documentData.signatureSendersStatement,
      aiClient: context.aiClient,
      isReadOnly: () => context.isLocked,
      label: { key: "dbot.custom-document.senders-statement" },
    });
    this.signatureSenders =
      context.documentData.signatureSenders?.map((x) => initSignatory(context, x)) ?? [];
    this.signatoriesStatement = new LocalizedTextEditorModel({
      texts: context.documentData.signatoriesStatement,
      aiClient: context.aiClient,
      isReadOnly: () => context.isLocked,
      label: { key: "dbot.custom-document.signatories-statement" },
    });
    this.signatories =
      context.documentData.signatories?.map((x) => initSignatory(context, x)) ?? [];

    this.customDocumentTypeHint = when(get(options, "customDocumentType"), (type) => {
      switch (type) {
        case "letter":
          return { key: "dbot.custom-document.type.letter.info" };
        case "agreement":
          return { key: "dbot.custom-document.type.agreement.info" };
      }
    });

    this.selectedCompany = context.orgNo;
    for (const c of getProjectCompanies(context)) {
      this.initRecipientsEditModel(c.orgNo);
    }

    this.onLanguageChange(context.documentData.documentLanguage);
    this.fetchTransactionLog();
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/remove-custom-panel":
        switch (event.id) {
          case "signature":
            this.set("signatureEnabled", false);
            break;
          case "header":
            this.set("headerEnabled", false);
            break;
          default:
            remove(this.blocks, (b) => b.id === event.id);
            break;
        }
        break;
      case "dbot/update-company-role-emails-result":
        this.onCompanyRoleUpdated(event);
        break;
      case "dbot/resend-notice-to-updated-recipient-result":
        if (event.documentId === this.context.documentId && isOk(event.result)) {
          this.onResendToRecipientResult(event.data);
        }
        break;
    }
  }

  getRecipients(orgNo: string = this.context.orgNo): Recipient[] {
    return this.getRecipientsEditModel(orgNo)?.toRecipients() ?? [];
  }

  getRecipientsEditModel(orgNo: string): RecipientsEditModel | undefined {
    return this.recipientsByCompany[orgNo];
  }

  initRecipientsEditModel(orgNo: string) {
    if (this.recipientsByCompany[orgNo]) {
      return;
    }
    const recipients =
      orgNo === this.context.orgNo
        ? (this.context.documentData.recipients ?? [])
        : (this.context.documentOverrides[orgNo]?.recipients ?? []);
    this.recipientsByCompany[orgNo] = new RecipientsEditModel(
      recipients,
      partial(this.onRecipientChange, orgNo),
    );
  }

  clearCustomDocumentTypeHint() {
    this.customDocumentTypeHint = undefined;
  }

  fetchTransactionLog() {
    if (this.blocks.some((b) => b.type === "transactionLog") && !this.transactionLog.length) {
      this.dispatch({
        type: "fetchTransactionLog",
        orgNo: this.context.orgNo,
        onSuccess: (transactions) => {
          this.transactionLog = transactions;
          this.blocks.forEach(partial(updateBlockTransactionLog, transactions));
        },
      });
    }
  }

  onLanguageChange(documentLangs?: Language[]) {
    this.title.onLanguageChange(documentLangs, this.context.documentData.title);
    this.signatureSendersStatement.onLanguageChange(
      documentLangs,
      this.context.documentData.signatureSendersStatement,
    );
    this.signatureSenders.forEach(
      partial(updateSignatoryLanguage, this.context.documentData.signatureSenders, documentLangs),
    );
    this.signatoriesStatement.onLanguageChange(
      documentLangs,
      this.context.documentData.signatoriesStatement,
    );
    this.signatories.forEach(
      partial(updateSignatoryLanguage, this.context.documentData.signatories, documentLangs),
    );
    this.blocks.forEach(partial(updateBlockLanguage, this.context, documentLangs));
  }

  onRecipientChange(orgNo: string, type: "add" | "remove" | "change", m: RecipientFieldModel) {
    this.recipientUpdateResult = undefined;
    const ref = resolveUrnReference(m.ref);
    if (type === "change") {
      switch (ref?.type) {
        case "companyroleId":
          this.dispatch(
            updateCompanyRoleEmailsEvent(orgNo, [
              { companyroleId: ref.id, email: m.emailField.validatedValue },
            ]),
          );
          break;
        case "shareholderId":
          this.dispatch({
            type: "dbot/update-shareholder-email",
            shareholderId: ref.id,
            email: m.emailField.validatedValue,
            onFulfilled: (result) => this.onShareholderRecipientUpdated(m, result),
          });
          break;
      }
    }
  }

  onCompanyRoleUpdated(event: UpdateCompanyRoleEmailsResultEvent) {
    this.recipientUpdateResult = event.result;
    if (isOk(event.result)) {
      for (const item of event.items) {
        this.recipientsByCompany[event.orgNo]?.recipientFields
          .find((r) => r.id === toCompanyRoleReference(item.companyroleId))
          ?.rebase();
      }
    }
  }

  onShareholderRecipientUpdated(
    recipient: RecipientFieldModel,
    result: Result<TMessage, TMessage>,
  ) {
    this.recipientUpdateResult = result;
    if (isOk(result)) {
      recipient.rebase();
    }
  }

  onResendToRecipientResult(data: ResendDocumentForViewing) {
    const companyRoleRef = toCompanyRoleReference(data.companyroleId ?? 0);
    const shareholderRef = toShareholderReference(data.shareholderId ?? 0);
    Object.values(this.recipientsByCompany).forEach((x) => {
      const field = x.recipientFields.find(
        (f) => f.ref === companyRoleRef || f.ref === shareholderRef,
      );
      field?.emailField.setAndCommitInputValue(data.recipientEmail);
    });
  }

  addBlock(type: CustomDocumentBlock["type"]) {
    const blockData = initNewBlockData(this.context, type);
    this.blocks.push(initBlockForm(this.context, this.transactionLog, this, blockData));
    this.fetchTransactionLog();
    this.dispatch([
      {
        type: "dbot/open-details-panel",
        panel: { type: "custom", id: blockData.id, documentId: this.context.documentId },
      },
    ]);
  }

  moveBlock(panel: DetailsPanel, toPanel: DetailsPanel) {
    const block = this.blocks.find((b) => b.id === panel.id);
    const toIndex = this.blocks.findIndex((b) => b.id === toPanel.id);
    if (block) {
      this.blocks = move(this.blocks, block, toIndex);
    }
  }

  setInsideAddressEnabled(enabled: boolean) {
    this.insideAddressEnabled = enabled;
  }

  addNewRecipient(orgNo: string) {
    this.getRecipientsEditModel(orgNo)?.addRecipient({
      id: newUuid(),
      name: "",
      email: "",
    });
  }

  addRecipients(orgNo: string, recipients: Recipient[]) {
    for (const r of recipients) {
      this.initRecipientsEditModel(orgNo);
      this.getRecipientsEditModel(orgNo)?.addRecipient(r);
    }
  }

  get recipientsAlert(): AlertProps | undefined {
    switch (this.recipientUpdateResult?.state) {
      case "ok":
        return {
          type: "success",
          message: this.recipientUpdateResult.data,
          onClose: this.dismissRecipientsAlert,
        };
      case "failure":
        return {
          type: "error",
          message: this.recipientUpdateResult.error,
          onClose: this.dismissRecipientsAlert,
        };
    }
  }

  dismissRecipientsAlert() {
    this.recipientUpdateResult = undefined;
  }

  closeSendCustomDocumentDialog() {
    this.sendDocumentDialogModel = undefined;
  }

  openSendCustomDocumentDialog(
    recipients: RecipientsEditModel = this.recipientsByCompany[this.context.orgNo],
  ) {
    this.dismissRecipientsAlert();
    this.sendDocumentDialogModel = {
      recipients: recipients,
      title: partial(getSendCustomDocumentTitle, this),
      emailBody: initEmailBody(this.context),
      alert: () => this.recipientsAlert,
      onClose: () => {
        this.dismissRecipientsAlert();
        this.closeSendCustomDocumentDialog();
      },
      onSubmit: this.sendDocument,
    };
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.context.documentId)!;
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.context.documentId);
  }

  get isSentForSigning() {
    return (
      isDocumentSentForSigning(this.context, this.context.documentId) ||
      isDocumentSigned(this.context, this.context.documentId)
    );
  }

  get openSendForSigningDialogAction() {
    return openSendForSigningDialogAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      showUnsupportedAttachmentWarning:
        this.context.attachments.length > 0 &&
        this.context.attachments.some((a) => !isExtensionPdf(a.filename)),
    });
  }

  get downloadSignedDocumentAction() {
    return downloadSignedDocumentAction({
      document: this.documentItem,
      signingStatus: this.documentStatus?.signingStatus,
      numberOfSignedDocuments: this.documentStatus?.numberOfSignedDocuments,
    });
  }

  get newRecipients() {
    const sentToIds =
      this.context.documentStatuses.find((s) => s.documentId === this.context.documentId)
        ?.viewerRecipientIds ?? [];
    if (!sentToIds.length) {
      return [];
    }
    return this.recipientsByCompany[this.context.orgNo].recipientFields.filter(
      (r) => !sentToIds.includes(String(r.id)),
    );
  }

  get hasNewRecipients() {
    return this.newRecipients.length > 0;
  }

  openSendToNewRecipientsDialog() {
    const m = new RecipientsEditModel([], partial(this.onRecipientChange, this.context.orgNo));
    m.recipientFields = this.newRecipients;
    this.openSendCustomDocumentDialog(m);
  }

  sendDocument() {
    const model = this.sendDocumentDialogModel;
    if (model?.recipients.validate()) {
      model.isSubmitting = true;
      this.dispatch({
        type: "dbot/send-document-for-viewing",
        documentId: this.context.documentId,
        emailBody: model.emailBody.localizedTexts[0].text,
        recipients: model.recipients.toRecipients(),
        onSuccess: this.closeSendCustomDocumentDialog,
        onFailure: this.closeSendCustomDocumentDialog,
      });
    }
  }

  get isValid(): boolean {
    return Object.values(this.recipientsByCompany).every((x) => x.isValid);
  }

  validate(): boolean {
    Object.values(this.recipientsByCompany).forEach((x) => {
      x.validate();
    });
    return this.isValid;
  }

  set<F extends keyof this>(field: F, value: this[F]) {
    this[field] = value;
  }

  addSignatory(list: "signatureSenders" | "signatories", option: CustomDocumentSignatoryOption) {
    this[list].push(
      initSignatory(this.context, {
        id: newUuid(),
        option,
        freetextRole: this.context.documentLanguages.map((language) => ({ language, text: "" })),
      }),
    );
  }

  moveSignatory(
    list: "signatureSenders" | "signatories",
    item: CustomDocumentSignatoryForm,
    toItem: CustomDocumentSignatoryForm,
  ) {
    this[list] = move(this[list], item, this[list].indexOf(toItem));
  }

  removeSignatory(list: "signatureSenders" | "signatories", item: CustomDocumentSignatoryForm) {
    this[list] = reject(this[list], item);
  }

  toggleCompany(orgNo: string, open: boolean = true) {
    if (!open) {
      this.selectedCompany = undefined;
    } else {
      this.selectedCompany = orgNo;
      this.initRecipientsEditModel(orgNo);
      this.dispatch([
        { type: "dbot/fetch-company-context-data", orgNo },
        { type: "dbot/select-company", orgNo },
      ]);
    }
  }
}

const initEmailBody = (context: DocumentContext<CustomDocumentData>): LocalizedTextEditorModel =>
  new LocalizedTextEditorModel({
    aiClient: context.aiClient,
    isReadOnly: () => false,
    label: { key: "customize-message" },
    texts: [{ language: context.locale, text: "" }],
  });

export const signatoryOptionLabel: Record<CustomDocumentSignatoryOption, TMessage> = {
  company: { key: "company" },
  ceo: {
    key: "dbot.custom-document.x-auto",
    params: { x: getRoleTypeLabel(RoleType.CEO) },
  },
  chairperson: {
    key: "dbot.custom-document.x-auto",
    params: { x: getRoleTypeLabel(RoleType.CHAIRPERSON) },
  },
  freetext: { key: "dbot.freetext" },
  recipient: {
    key: "dbot.custom-document.x-auto",
    params: { x: { key: "dbot.recipient" } },
  },
};

const signatureSenderOptions: CustomDocumentSignatoryOption[] = [
  "company",
  "ceo",
  "chairperson",
  "freetext",
];

const signatureSignatoryOptions: CustomDocumentSignatoryOption[] = [
  "recipient",
  "ceo",
  "chairperson",
  "freetext",
];

const initSignatory = (
  context: DocumentContext<CustomDocumentData>,
  s: CustomDocumentSignatory,
): CustomDocumentSignatoryForm => ({
  id: s.id,
  option: s.option,
  freetextName: new StringFieldModel({ value: s.freetextName }),
  freetextRole: new LocalizedTextEditorModel({
    texts: s.freetextRole,
    aiClient: context.aiClient,
    isReadOnly: () => context.isLocked,
    label: { key: "signatures" }, // not used
  }),
});

const initNewBlockData = (
  context: DocumentContext<CustomDocumentData>,
  type: CustomDocumentBlock["type"],
): CustomDocumentBlock => {
  switch (type) {
    case "freetext":
      return {
        type: "freetext",
        id: newUuid(),
        heading:
          context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
        text: context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
      };
    case "shareholders":
      return {
        type: "shareholders",
        id: newUuid(),
      };
    case "transactionLog":
      return {
        type: "transactionLog",
        id: newUuid(),
        heading:
          context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
        from: new Date().getFullYear() - 1 + "-01-01",
        to: new Date().getFullYear() - 1 + "-12-31",
        transactionTypes: Object.values(TransactionType),
      };
    case "personalDrawdownContribution":
      return {
        type: "personalDrawdownContribution",
        id: newUuid(),
        heading:
          context.documentData.documentLanguage?.map((l) => ({ language: l, text: "" })) ?? [],
      };
  }
};

const initBlockForm = (
  context: DocumentContext<CustomDocumentData>,
  transactionsLog: Transaction[],
  form: CustomDocumentForm,
  block: CustomDocumentBlock,
): CustomDocumentBlockForm => {
  const includeShareCapital = context.jsonData.decisions.some(
    (d) => d.decisionData.type === DecisionType.increaseOfShareCapital,
  );
  switch (block.type) {
    case "freetext":
      return {
        type: "freetext",
        id: block.id,
        heading: new LocalizedTextEditorModel({
          texts: block.heading,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.heading" },
        }),
        text: new LocalizedTextEditorModel({
          texts: block.text,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.text" },
          dataProvider: {
            getData: context.getData,
            includeShareCapital,
          },
        }),
      };
    case "shareholders": {
      const perDate = new LocalDateFieldModel({ value: block.perDate });
      const currentOrPerDate = makeCurrentOrPerDateRadioModel(perDate);
      return {
        type: "shareholders",
        id: block.id,
        currentOrPerDate,
        perDate,
        get showDateInput() {
          return currentOrPerDate.value === "per-date";
        },
      };
    }
    case "transactionLog": {
      const from = new LocalDateFieldModel({
        value: block.from,
        maxDate: block.to,
        required: true,
      });
      const to = new LocalDateFieldModel({ value: block.to, minDate: block.from, required: true });
      return {
        type: "transactionLog",
        id: block.id,
        heading: new LocalizedTextEditorModel({
          texts: block.heading,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.heading" },
        }),
        from,
        to,
        transactionTypes: new OptionFieldModel({
          value: block.transactionTypes,
          options: () => [
            ...new Set(
              transactionsLog
                .filter(
                  (a) =>
                    isBefore(block.from, a.transactionDate) && isAfter(block.to, a.transactionDate),
                )
                .map((d) => TransactionType[d.transactionType]),
            ),
          ],
          idFn: (x) => x,
          labelFn: (value) => ({ key: value }),
        }),
        initTransactionTypes: block.transactionTypes,
      };
    }
    case "personalDrawdownContribution": {
      return {
        type: "personalDrawdownContribution",
        id: block.id,
        heading: new LocalizedTextEditorModel({
          texts: block.heading,
          aiClient: context.aiClient,
          isReadOnly: () => context.isLocked,
          label: { key: "dbot.heading" },
        }),
      };
    }
  }
};

const makeCurrentOrPerDateRadioModel = (
  perDate: LocalDateFieldModel,
): RadioFieldModel<"current" | "per-date"> =>
  new RadioFieldModel<"current" | "per-date">({
    options: ["current", "per-date"],
    value: perDate.isEmpty ? "current" : "per-date",
    idFn: identity,
    labelFn: (x) => {
      switch (x) {
        case "current":
          return { key: "current" };
        case "per-date":
          return { key: "dbot.per-date" };
      }
    },
    onChange: () => {
      perDate.setAndCommitInputValue("");
    },
  });

const freetextByIdPred =
  (id: string) =>
  (x: CustomDocumentBlock): x is CustomDocumentFreetextBlock =>
    x.type === "freetext" && x.id === id;

const updateBlockLanguage = (
  context: DocumentContext<CustomDocumentData>,
  documentLangs: Language[] | undefined,
  form: CustomDocumentBlockForm,
) => {
  const data = context.documentData.blocks?.find(freetextByIdPred(form.id));
  switch (form.type) {
    case "freetext":
      form.heading.onLanguageChange(documentLangs, data?.heading ?? []);
      form.text.onLanguageChange(documentLangs, data?.text ?? []);
      break;
    case "transactionLog":
      form.heading.onLanguageChange(documentLangs, data?.heading ?? []);
      break;
    case "personalDrawdownContribution":
      form.heading.onLanguageChange(documentLangs, data?.heading ?? []);
      break;
  }
};

const updateSignatoryLanguage = (
  sourceList: CustomDocumentSignatory[] | undefined,
  documentLangs: Language[] | undefined,
  form: CustomDocumentSignatoryForm,
) => {
  form.freetextRole.onLanguageChange(
    documentLangs,
    sourceList?.find((x) => x.id === form.id)?.freetextRole,
  );
};

const updateBlockTransactionLog = (transactions: Transaction[], form: CustomDocumentBlockForm) => {
  switch (form.type) {
    case "transactionLog":
      form.transactionTypes.updateOptions(() => [
        ...new Set(
          transactions
            .filter(
              (a) =>
                isBefore(form.from.toISOString() ?? "", a.transactionDate) &&
                isAfter(form.to.toISOString() ?? "", a.transactionDate),
            )
            .map((d) => TransactionType[d.transactionType]),
        ),
      ]);
      form.transactionTypes.setSelected(form.initTransactionTypes);
      break;
  }
};

const applySignatoryForm = (form: CustomDocumentSignatoryForm): CustomDocumentSignatory => ({
  id: form.id,
  option: form.option,
  freetextName: form.option === "freetext" ? form.freetextName.validatedValue : undefined,
  freetextRole: form.option === "freetext" ? form.freetextRole.localizedTexts : undefined,
});

export const applyForm = (form: CustomDocumentForm): CustomDocumentData => ({
  type: DocumentType.custom_document,
  documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
  title: form.title.localizedTexts,
  insideAddressEnabled: form.insideAddressEnabled,
  insideAddressOption: form.insideAddressOption.value,
  insideAddressFreetext: form.insideAddressFreetext.validatedValue,
  headerEnabled: form.headerEnabled,
  headerLogoEnabled: form.headerLogoEnabled,
  headerCompanyNameEnabled: form.headerCompanyNameEnabled,
  headerTitlesEnabled: form.headerTitlesEnabled,
  headerDateEnabled: form.headerDateEnabled,
  documentDateOption: form.documentDateOption.value,
  customDocumentDate: form.customDocumentDate.validatedValue,
  recipients: form.getRecipients(),
  blocks: form.blocks.map((b) => {
    switch (b.type) {
      case "freetext":
        return {
          type: "freetext",
          id: b.id,
          heading: b.heading.localizedTexts,
          text: b.text.localizedTexts,
        };
      case "shareholders":
        return {
          type: "shareholders",
          id: b.id,
          perDate: b.perDate.validatedValue,
        };
      case "transactionLog":
        return {
          type: "transactionLog",
          id: b.id,
          heading: b.heading.localizedTexts,
          from: b.from.validatedValue ?? "",
          to: b.to.validatedValue ?? "",
          transactionTypes: b.transactionTypes?.selected ?? [],
        };
      case "personalDrawdownContribution":
        return {
          type: "personalDrawdownContribution",
          id: b.id,
          heading: b.heading.localizedTexts,
        };
    }
  }),
  signatureEnabled: form.signatureEnabled,
  signatureSendersStatement: form.signatureSendersStatement.localizedTexts,
  signatureSenders: form.signatureSenders.map(applySignatoryForm),
  signatoriesStatement: form.signatoriesStatement.localizedTexts,
  signatories: form.signatories.map(applySignatoryForm),
});

export const applyOverrides = (
  form: CustomDocumentForm,
  orgNo: string,
): CustomDocumentOverrides | undefined => {
  return {
    type: DocumentType.custom_document,
    recipients: form.recipientsByCompany[orgNo]?.toRecipients(),
  };
};

export const getCustomDocumentTitle = (
  form: CustomDocumentForm,
  lang: Language,
): TMessage | undefined => {
  const text =
    form.title.localizedTexts.find((l) => l.language === lang)?.text ??
    first(form.title.localizedTexts)?.text;
  if (text) {
    return { translated: text };
  }
};

export const getCustomDocumentSettingsPanels = (form: CustomDocumentForm): DetailsPanel[] => [
  {
    type: "custom",
    id: "document-settings",
    documentId: form.context.documentId,
    icon: "gear",
    heading: { key: "dbot.setup" },
    label: form.documentLanguage.getLabel(form.documentLanguage.value),
    isDisabled: form.context.isLocked,
  },
  {
    type: "custom",
    id: "recipients",
    documentId: form.context.documentId,
    icon: "users",
    heading: { key: "dbot.recipients" },
    label: form.recipientsByCompany.isEmpty
      ? { key: "none" }
      : {
          translated: String(
            sumBy(Object.values(form.recipientsByCompany), (form) => form.recipientFields.length),
          ),
        },
  },
];

export const getCustomDocumentHeaderPanels = (form: CustomDocumentForm): DetailsPanel[] => [
  {
    type: "custom",
    id: "header",
    documentId: form.context.documentId,
    icon: "article",
    heading: { key: "dbot.custom-document.header" },
    canRemove: !form.context.isLocked,
  },
];

export const getCustomDocumentSignaturePanels = (form: CustomDocumentForm): DetailsPanel[] => [
  {
    type: "custom",
    id: "signature",
    documentId: form.context.documentId,
    icon: "signature",
    heading: { key: "signatures" },
    canRemove: !form.context.isLocked,
  },
];

export const getCustomDocumentBlockPanels = (form: CustomDocumentForm): DetailsPanel[] =>
  form.blocks.map<DetailsPanel>((b) => {
    switch (b.type) {
      case "freetext":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "text-lines",
          heading: customBlockLabels.freetext,
          summaryHeading: when(
            b.heading.localizedTexts[0]?.text || b.text.localizedTexts[0]?.text,
            (text) => ({
              translated: text,
            }),
          ),
          label: { key: "dbot.freetext" },
          canRemove: !form.context.isLocked,
        };
      case "shareholders":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "table",
          heading: customBlockLabels.shareholders,
          label: { key: "dbot.table" },
          canRemove: !form.context.isLocked,
        };
      case "transactionLog":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "table",
          heading: customBlockLabels.transactionLog,
          label: { key: "dbot.table" },
          canRemove: !form.context.isLocked,
        };
      case "personalDrawdownContribution":
        return {
          type: "custom",
          id: b.id,
          documentId: form.context.documentId,
          icon: "code-block",
          heading: customBlockLabels.personalDrawdownContribution,
          label: { key: "dbot.drawdown" },
          canRemove: !form.context.isLocked,
        };
    }
  });

export const getCustomDocumentPanels = (form: CustomDocumentForm): DetailsPanel[] => [
  ...getCustomDocumentSettingsPanels(form),
  ...getCustomDocumentHeaderPanels(form),
  ...getCustomDocumentBlockPanels(form),
  ...getCustomDocumentSignaturePanels(form),
];

export const autofocusHeading = (block: CustomDocumentFreetextBlockForm): boolean =>
  !block.heading.isEmpty;

export const autofocusText = (block: CustomDocumentFreetextBlockForm): boolean =>
  block.heading.isEmpty;

export const showAutoTranslateOptions = (form: CustomDocumentForm): boolean =>
  mapDocumentLanguageToLocales(form.documentLanguage.value).length > 1;

export const isRecipientNameDisabled = (r: RecipientFieldModel): boolean => r.ref !== undefined;

const shareholderToRecipient = (s: ExtendedShareholderDTO): Recipient => ({
  id: newUuid(),
  name: s.name,
  email: s.email,
  att: s.isCompany ? getFullName(s.contactInfo) : undefined,
  ref: toShareholderReference(s.id),
});

const companyRoleToRecipient = (r: CompanyRole): Recipient => ({
  id: newUuid(),
  name: getFullName(r),
  email: r.email ?? "",
  ref: toCompanyRoleReference(r.companyroleId),
});

const subscriberToRecipient = (s: Subscriber): Recipient => ({
  id: newUuid(),
  name: s.name,
  email: s.email,
  ref: toSubscriberIdReference(s.subscriberId),
});

export const getSendCustomDocumentTitle = (form: CustomDocumentForm, lang: Language): TMessage => ({
  key: "dbot.send-document",
  params: {
    name: getCustomDocumentTitle(form, lang) ?? {
      type: "uncapitilize",
      value: { key: "document" },
    },
  },
});

export const prepAddBlockDropdown = (form: CustomDocumentForm): DropdownMenuProps | undefined =>
  form.context.isLocked
    ? undefined
    : {
        button: {
          type: "button",
          variant: "secondary",
          label: { key: "dbot.add-section" },
          icon: "plus",
        },
        items: compact([
          !form.headerEnabled && {
            type: "item",
            label: { key: "dbot.custom-document.header" },
            icon: "article",
            onSelect: () => form.set("headerEnabled", true),
          },
          {
            type: "item",
            label: customBlockLabels.freetext,
            icon: "text-lines",
            onSelect: () => form.addBlock("freetext"),
          },
          {
            type: "item",
            label: customBlockLabels.shareholders,
            icon: "table",
            onSelect: () => form.addBlock("shareholders"),
          },
          {
            type: "item",
            label: customBlockLabels.transactionLog,
            icon: "table",
            onSelect: () => form.addBlock("transactionLog"),
          },
          form.context.subscribers.length > 0 && {
            type: "item",
            label: customBlockLabels.personalDrawdownContribution,
            icon: "code-block",
            onSelect: () => form.addBlock("personalDrawdownContribution"),
          },
          !form.signatureEnabled && {
            type: "item",
            label: { key: "signatures" },
            icon: "signature",
            onSelect: () => form.set("signatureEnabled", true),
          },
        ]),
      };

export const prepAddSenderItems = (form: CustomDocumentForm): DropdownMenuItems =>
  signatureSenderOptions
    .filter((x) => {
      switch (x) {
        case "freetext":
          return true;
        default:
          return !form.signatureSenders.some((y) => y.option === x);
      }
    })
    .map((x) => ({
      type: "item",
      id: x,
      label: signatoryOptionLabel[x],
      onSelect: () => form.addSignatory("signatureSenders", x),
    }));

export const prepAddSignatoryItems = (form: CustomDocumentForm): DropdownMenuItems =>
  signatureSignatoryOptions
    .filter((x) => {
      switch (x) {
        case "freetext":
          return true;
        default:
          return !form.signatories.some((y) => y.option === x);
      }
    })
    .map((x) => ({
      type: "item",
      id: x,
      label: signatoryOptionLabel[x],
      onSelect: () => form.addSignatory("signatories", x),
    }));

const quickAddSubscribers = (subscribers?: Subscriber[]): Recipient[] => {
  return subscribers?.map((s) => subscriberToRecipient(s)) ?? [];
};

const quickAddShareholders = (shareholders?: ExtendedShareholderDTO[]): Recipient[] => {
  return filterActiveShareholders(shareholders ?? []).map(shareholderToRecipient);
};

const quickAddBoardMembers = (roles?: CompanyRole[]): Recipient[] => {
  return sortedRoles(roles ?? [])
    .filter(isBoardMember)
    .map(companyRoleToRecipient);
};

const quickAddCeo = (roles?: CompanyRole[]): Recipient | undefined => {
  return when(
    roles?.find((r) => r.role === RoleType.CEO),
    companyRoleToRecipient,
  );
};

const quickAddChairperson = (roles?: CompanyRole[]): Recipient | undefined => {
  return when(
    roles?.find((r) => r.role === RoleType.CHAIRPERSON),
    companyRoleToRecipient,
  );
};

export const quickAdd = (form: CustomDocumentForm, orgNo: string): QuickAddItem[] => {
  const companyContext = isOk(form.context.companyContexts[orgNo])
    ? form.context.companyContexts[orgNo].data
    : undefined;
  return compact<[string, TMessage, Recipient | Recipient[]]>([
    companyContext?.subscribers && [
      "subscribers",
      { key: "subscribers.title" },
      quickAddSubscribers(companyContext.subscribers),
    ],
    [
      "shareholders",
      { key: "actors.type.shareholder" },
      quickAddShareholders(companyContext?.shareholderData.shareholders),
    ],
    [
      "board-members",
      { key: "actors.type.board-members" },
      quickAddBoardMembers(companyContext?.companyInfo.roles),
    ],
    when(quickAddCeo(companyContext?.companyInfo.roles), (ceo) => [
      "ceo",
      { key: "ceo-with-name", params: { name: ceo.name } },
      ceo,
    ]),
    when(quickAddChairperson(companyContext?.companyInfo.roles), (chairperson) => [
      "chairperson",
      { key: "chairperson-with-name", params: { name: chairperson.name } },
      chairperson,
    ]),
  ]).map(([name, label, recipients]) => ({
    name,
    label,
    count: Array.isArray(recipients) ? recipients.length : undefined,
    disabled: Array.isArray(recipients) && !recipients.length,
    recipients: Array.isArray(recipients) ? recipients : [recipients],
  }));
};
